import React, { useCallback, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import useFetch from '../../../hooks/useFetch';
import '../styles/MatchingQuestion.css';

const PracticeMatching = () => {
  const authState = useSelector(state => state.authReducer);
  const [fetchData, { loading }] = useFetch();
  const [questionList, setQuestionList] = useState([]);
  const [matchingQCount, setMatchingQCount] = useState(0);

  const [questionData, setQuestionData] = useState(null);
  const [selectedA, setSelectedA] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [detailsAnswers, setDetailsAnswers] = useState([]);
  const [selectedB, setSelectedB] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState([]);

  const [colorIndex, setColorIndex] = useState(0);

  const [result, setResult] = useState(null);
  const [detailsResult, setDetailsResult] = useState(null);

 const colors = [
  'bg-red-500',    // Red
  'bg-blue-500',   // Blue
  'bg-green-500',  // Green
  'bg-yellow-500', // Yellow
  'bg-purple-500', // Purple
  'bg-pink-500',   // Pink
  'bg-teal-500',   // Teal
  'bg-orange-500', // Orange
  'bg-indigo-500', // Indigo
  'bg-gray-500'    // Gray
];
  const fetchMatchingQList = useCallback(() => {
    const config = {
      url: '/matchingq',
      method: 'get',
      headers: { Authorization: authState.token },
    };

    fetchData(config, { showSuccessToast: false })
      .then(response => {
        if (response && Array.isArray(response)) {
          setQuestionList(response);
          setMatchingQCount(response.length);
          setHasMatchinListClicked(true);
        } else {
          setHasMatchinListClicked(true);
        }
      })
      .catch(error => {
        console.error('Error Fetching MatchingQList', error);
      });
  }, [authState.token, fetchData]);

  useEffect(() => {
    if (authState.isLoggedIn) {
      fetchMatchingQList();
    }
  }, [authState.isLoggedIn, fetchMatchingQList]);

  const handleQuestionClick = (question) => {
    setQuestionData(question);
    setAnswers(Array(question.numberOfMatches).fill(null));
    setResult(null);
    setDetailsResult(null);
    setSelectedA([]);
    setSelectedColors([]); // Ensure this is an array
  };

  const handleSelectA = (index) => {
    if (!Array.isArray(selectedColors)) {
      setSelectedColors([]); // Reset to array if not an array
    }

    let updatedSelectedA;
    let updatedSelectedColors;

    if (selectedA.includes(index)) {
      // Remove the item from the selection
      updatedSelectedA = selectedA.filter(item => item !== index);
      updatedSelectedColors = selectedColors.filter((_, i) => i !== selectedA.indexOf(index));
    } else {
      // Add the item to the selection with a new color
      updatedSelectedA = [...selectedA, index];
      const newColorIndex = (colorIndex + 1) % colors.length;
      updatedSelectedColors = [...selectedColors, colors[newColorIndex]];
      setColorIndex(newColorIndex);
    }

    setSelectedA(updatedSelectedA);
    setSelectedColors(updatedSelectedColors);
  };

  const handleSelectB = (index) => {
    if (selectedA.length > 0) {
      const selectedColor = selectedColors[selectedA.length - 1];
      if (!selectedB.find(b => b.index === index)) {
        setSelectedB([...selectedB, { index, color: selectedColor }]);
        setAnswers([...answers, { index, ans: String.fromCharCode(index + 65) }]);
      }
    }
  };

  const handleSubmit = () => {
    const correctAnswers = questionData.correctAnswers.split(',').map(pair => pair.split('-')[1]);
    const userAnswers = answers.map((answer) => (answer !== null ? String.fromCharCode(65 + answer) : ''));
    const isCorrect = userAnswers.map((ans, index) => ans === correctAnswers[index]);

    setResult({ userAnswers, isCorrect });

    const detailsAnswers = answers.map((answer, index) => ({
      userAnswer: answer !== null ? String.fromCharCode(65 + answer) : '',
      detailsAnswer: questionData.question.split('<div>')[index + 1]?.replace('</div>', '').trim() || '',
      isCorrect: answer === correctAnswers[index],
    }));
    setDetailsResult(detailsAnswers);
  };

  return (
    <div className="min-h-screen bg-black py-2">
        <div className='border-b border-blue-400 mb-2'>
            <h2 className="text-lg lg:text-xl font-extrabold text-center text-yellow-200 mb-2 p-2">
                📖 Practice Matching Questions
            </h2>
        </div>

      <div className="min-h-[85vh] mx-auto px-4">
        {questionList.length === 0 ? (
          <div className="flex justify-center mt-8">
            <button
              onClick={fetchMatchingQList}
              className='bg-blue-600 text-white px-4 py-2 rounded shadow-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500'
            >
              Get Matching Questions List
            </button>
          </div>
        ) : (
          <>
            <div className='text-center my-2'>
              <p className='text-m lg:text-m font-semibold text-gray-200'>{matchingQCount} Questions Found!</p>
            </div>

            <div className="space-y-6">
              {questionList.map((question, index) => (
                <div key={index} className="bg-gray-800 rounded-lg p-6 shadow-lg">
                  <button
                    className='w-full text-left bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500'
                    onClick={() => handleQuestionClick(question)}
                  >
                    <span className='font-semibold'>{`Question ${index + 1}.`}</span> {question.questionTitle}
                  </button>

                  {questionData && questionData._id === question._id && (
                    <div className="mt-6">
                      <div className="flex space-x-4">
                        <div className="flex-1 bg-black p-4 rounded-lg text-white">
                          {questionData.question.split('<div>').slice(1, questionData.numberOfMatches + 1).map((item, index) => (
                            <div
                              key={index}
                              className={`cursor-pointer p-2 rounded-lg ${selectedA.includes(index) ? selectedColors[selectedA.indexOf(index)] : 'bg-gray-600 hover:bg-gray-500'}`}
                              onClick={() => handleSelectA(index)}
                            >
                              {item.replace('</div>', '')}
                            </div>
                          ))}
                        </div>

                        <div className="flex-1 bg-black p-4 rounded-lg text-white">
                          {questionData.question.split('<div>').slice(questionData.numberOfMatches + 1).map((item, index) => (
                            <div
                              key={index}
                              className={`cursor-pointer p-2 rounded-lg ${selectedB.find(b => b.index === index)?.color || 'bg-gray-600 hover:bg-gray-500'}`}
                              onClick={() => handleSelectB(index)}
                            >
                              {item.replace('</div>', '')}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="flex justify-center mt-6">
                        <button
                          className='bg-green-600 text-white px-4 py-2 rounded shadow-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500'
                          onClick={handleSubmit}
                        >
                          Done
                        </button>
                      </div>

                      {result && (
                        <>
                          <div className="mt-6 bg-purple-200 p-4 rounded-lg text-gray-800">
                            <h4 className='text-lg font-semibold'>Results:</h4>
                            <div className="flex flex-wrap">
                              {result.userAnswers.map((answer, index) => (
                                <span key={index} className={`mr-4 text-sm ${result.isCorrect[index] ? 'text-green-600' : 'text-red-600'}`}>
                                  {index + 1} - {answer} is {result.isCorrect[index] ? 'Correct' : 'Wrong'}
                                </span>
                              ))}
                            </div>
                          </div>

                          <div className="mt-6 bg-black p-4 rounded-lg text-white">
                            <h4 className='text-lg font-semibold'>Correct Answers:</h4>
                            <p>{questionData.correctAnswers}</p>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <footer className="bg-black border-t border-blue-400 text-gray-400 py-4">
        <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-sm">© 2025 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
    </footer>  
  </div>
  );
};

export default PracticeMatching;
